import { useState } from "react";

import {
  useDeactivateAllWorkbenchApiKeys,
  useGetWorkbenchApiKeys,
} from "@vega-api/generated/api-gateway-workbench-v1/api-gateway-workbench-v1";
import { InputControl } from "@vega-core/components/Form/Controls/Input/InputControl";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Notice } from "@vega-core/components/Notice/Notice";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";

import { deriveKeyStatus, KeyStatus } from "./deriveKeyStatus";

const CONFIRM_TEXT = "DEACTIVATE";

type DeactivateAllKeysDialogProps = {
  userId: string;
  userName: string;
  onClose: VoidFunction;
  onDeactivated: VoidFunction;
};

/** Kill switch: high-friction, type-to-confirm deactivation of every key of a user. */
export const DeactivateAllKeysDialog = ({
  userId,
  userName,
  onClose,
  onDeactivated,
}: DeactivateAllKeysDialogProps) => {
  const [confirmInput, setConfirmInput] = useState("");
  const showToast = useToast();

  // The listing is server-paginated, so the dialog fetches the user's own
  // keys to list exactly what the kill switch is about to deactivate.
  const { data: userKeysResponse } = useGetWorkbenchApiKeys({
    userId,
    pageSize: 500,
  });
  const activeKeys = (userKeysResponse?.data ?? []).filter(
    (key) => deriveKeyStatus(key) !== KeyStatus.Deactivated,
  );

  const { mutate: deactivateAll, isPending } = useDeactivateAllWorkbenchApiKeys(
    {
      mutation: {
        onSuccess: () => {
          showToast({
            title: m.settings_reportingApiKeys_toast_allDeactivated(),
            variant: "success",
          });
          onDeactivated();
          onClose();
        },
        onError: () => {
          showToast({
            title: m.settings_reportingApiKeys_toast_error(),
            variant: "destructive",
          });
        },
      },
    },
  );

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainActionText: m.settings_reportingApiKeys_deactivateAll_confirm(),
        mainActionVariant: "destructive",
        mainAction: () => deactivateAll({ userId }),
        disabled: confirmInput !== CONFIRM_TEXT,
        isLoading: isPending,
        secondaryActionText: m.btn_cancel(),
        secondaryAction: onClose,
      }}
      dataTestId="reporting-api-keys-deactivate-all-dialog"
      open
      setOpen={(value) => {
        if (!value) onClose();
      }}
      title={m.settings_reportingApiKeys_deactivateAll_title()}
    >
      <div className="flex flex-col gap-4 text-left">
        <Notice
          content={`${m.settings_reportingApiKeys_deactivateAll_warning({ userName })} ${m.settings_reportingApiKeys_noGracePeriod()}`}
          title={m.settings_reportingApiKeys_deactivateAll_title()}
          type="warning"
          variant="accent"
        />
        {activeKeys.length > 0 ? (
          <div>
            <p className="body-base-medium mb-1">
              {m.settings_reportingApiKeys_deactivateAll_activeKeys({
                count: activeKeys.length,
              })}
            </p>
            <ul className="list-disc pl-5 body-base-regular text-text/secondary">
              {activeKeys.map((key) => (
                <li key={key.keyName}>{key.keyName}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <InputControl
          label={m.settings_reportingApiKeys_deactivateAll_typeToConfirm()}
          name="reporting-api-keys-deactivate-all-confirm"
          onChange={(event) => setConfirmInput(event.target.value)}
          placeholder={CONFIRM_TEXT}
          type="text"
          value={confirmInput}
          withBorder
        />
      </div>
    </ActionaryModal>
  );
};
